import React from "react";
import { cn } from "@/lib/utils";

interface OverviewCardProps {
  /** Leading icon, typically a 14px lucide glyph. */
  icon: React.ReactNode;
  title: string;
  /** Optional count pill shown next to the title. */
  count?: number | string;
  /** Optional trailing action rendered right-aligned in the header. */
  action?: React.ReactNode;
  /** Rendered directly under the header, outside the padded body. */
  beforeBody?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * The card shell every Overview section uses: bordered container, a header row
 * with icon/title/count/action, then the body.
 *
 * Exists because four cards share this exact header and `ui/card.tsx` exports
 * only `Card` with no sub-components. Deliberately local to the Overview
 * feature rather than added to `ui/` — revisit promoting it if a third feature
 * needs the same shape.
 *
 * Note `bg-card` is intentionally absent: in dark mode `--card` is identical to
 * `--background`, so the border is what separates the card from the page.
 */
const OverviewCard: React.FC<OverviewCardProps> = ({
  icon,
  title,
  count,
  action,
  beforeBody,
  children,
  className,
}) => (
  <div
    className={cn(
      "border border-border rounded-xl overflow-hidden mb-5",
      className,
    )}
  >
    <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border">
      <span className="text-muted-foreground flex-none">{icon}</span>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {count !== undefined && (
        <span className="text-[11px] font-semibold text-muted-foreground bg-accent rounded-full px-2 py-px">
          {count}
        </span>
      )}
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {beforeBody}
    <div className="py-1.5">{children}</div>
  </div>
);

export default OverviewCard;
